import type { RegistryEntry, UiOption } from './types';
import { xaiConfigs } from './configs/xai';
import { fluxConfigs } from './configs/flux';
import { kreaConfigs } from './configs/krea';
import { klingConfigs } from './configs/kling';
import { microsoftConfigs } from './configs/microsoft';
import { lumaConfigs } from './configs/luma';
import { nvidiaConfigs } from './configs/nvidia';
import { wavespeedConfigs } from './configs/wavespeed';
import { ideogramConfigs } from './configs/ideogram';
import { googleConfigs } from './configs/google';
import { openaiConfigs } from './configs/openai';
import { davinciConfigs } from './configs/davinci';
import { segmindConfigs } from './configs/segmind';
import { topazConfigs } from './configs/topaz';

export type {
  UiOption,
  UiOptionSwitch,
  UiOptionSlider,
  UiOptionNumber,
  UiOptionSelect,
  UiOptionInput,
} from './types';

/**
 * 各厂商/模型族的 UI 选项配置，按文件拆分（见 ./configs）。
 * 新增模型时，请在对应厂商的 config 文件里添加，并在此处合并。
 */
const registry: Record<string, RegistryEntry> = {
  ...xaiConfigs,
  ...fluxConfigs,
  ...kreaConfigs,
  ...klingConfigs,
  ...microsoftConfigs,
  ...lumaConfigs,
  ...nvidiaConfigs,
  ...wavespeedConfigs,
  ...ideogramConfigs,
  ...googleConfigs,
  ...openaiConfigs,
  ...davinciConfigs,
  ...segmindConfigs,
  ...topazConfigs,
};

export const getUiOptionsForModel = (modelId: string): UiOption[] =>
  registry[modelId]?.options ?? [];

/**
 * Returns { key: default } for every option with group === 'hidden'.
 * Used to pre-fill modelUi so hidden defaults are always present.
 */
export const getHiddenDefaults = (modelId: string): Record<string, any> => {
  const entry = registry[modelId];
  if (!entry) return {};
  const defaults: Record<string, any> = {};
  for (const opt of entry.options) {
    if (opt.group === 'hidden' && 'default' in opt) {
      defaults[opt.key] = (opt as any).default;
    }
  }
  return defaults;
};

export const buildProviderOptions = (
  modelId: string,
  ui: Record<string, any>
): Record<string, Record<string, any>> => {
  const entry = registry[modelId];
  if (!entry) return {};
  const hiddenDefaults = getHiddenDefaults(modelId);
  const merged = Object.keys(hiddenDefaults).length
    ? { ...hiddenDefaults, ...ui }
    : ui;
  return entry.toProviderOptions(merged);
};